// Zaveato Services Frontend - Public appointment view with QR code support
import { createApp } from 'vue'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import Tooltip from 'primevue/tooltip'
import Aura from '@primevue/themes/aura'

import App from './App.vue'
import router from './router'
import i18n from './i18n'
import { initializeApiInterceptors, setPiniaInstance } from './services/api'
import { installGlobalErrorReporter } from './services/errorReporter'
import { useAuthStore } from './stores/auth'
import { useBrandStore } from './stores/brand'
import { initializeGuestService } from './services/guestService'
import { errorService } from './services/errorService'
import { googleAnalytics } from './services/googleAnalytics'
import { initSentry, captureHandledError } from './services/sentry'

// Initialize Google Analytics early (with consent mode - default denied)
googleAnalytics.init()

// Wire the admin-audit client-error reporter (window.error +
// unhandledrejection). Idempotent — safe if HMR re-runs main.ts.
installGlobalErrorReporter()

// Styles
import 'primeicons/primeicons.css'
import './assets/main.css'
import './assets/theme.css'

// Boot the shared light/dark theme BEFORE mounting the app so the first
// render already has `data-theme` + `.dark` on <html>. The composable's
// module-level init stamps the DOM synchronously; the import itself is
// what triggers that. No side-effects if this session hasn't picked a
// theme yet — it just applies the OS preference.
import '@/composables/useLandingTheme'

const app = createApp(App)

// Initialize Sentry as early as possible so startup errors are captured too.
// Must happen after createApp() but before app.mount().
initSentry(app, router)

// Pinia Store - create instance and save reference for API module
const pinia = createPinia()
app.use(pinia)

// Pass Pinia instance to API module for auth store access
setPiniaInstance(pinia)

// Vue Router
app.use(router)

// Vue i18n
app.use(i18n)

// PrimeVue
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      prefix: 'p',
      darkModeSelector: '.dark',
      cssLayer: false
    }
  },
  ripple: true,
  inputStyle: 'outlined',
  locale: {
    startsWith: 'Starts with',
    contains: 'Contains',
    notContains: 'Not contains',
    endsWith: 'Ends with',
    equals: 'Equals',
    notEquals: 'Not equals',
    noFilter: 'No Filter',
    filter: 'Filter',
    lt: 'Less than',
    lte: 'Less than or equal to',
    gt: 'Greater than',
    gte: 'Greater than or equal to',
    dateIs: 'Date is',
    dateIsNot: 'Date is not',
    dateBefore: 'Date is before',
    dateAfter: 'Date is after',
    clear: 'Clear',
    apply: 'Apply',
    matchAll: 'Match All',
    matchAny: 'Match Any',
    addRule: 'Add Rule',
    removeRule: 'Remove Rule',
    accept: 'Yes',
    reject: 'No',
    choose: 'Choose',
    upload: 'Upload',
    cancel: 'Cancel',
    completed: 'Completed',
    pending: 'Pending',
    dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    chooseYear: 'Choose Year',
    chooseMonth: 'Choose Month',
    chooseDate: 'Choose Date',
    prevDecade: 'Previous Decade',
    nextDecade: 'Next Decade',
    prevYear: 'Previous Year',
    nextYear: 'Next Year',
    prevMonth: 'Previous Month',
    nextMonth: 'Next Month',
    prevHour: 'Previous Hour',
    nextHour: 'Next Hour',
    prevMinute: 'Previous Minute',
    nextMinute: 'Next Minute',
    prevSecond: 'Previous Second',
    nextSecond: 'Next Second',
    am: 'AM',
    pm: 'PM',
    today: 'Today',
    weekHeader: 'Wk',
    firstDayOfWeek: 1, // Monday for UK/EU
    showMonthAfterYear: false,
    dateFormat: 'dd/mm/yy', // UK date format
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    passwordPrompt: 'Enter a password',
    emptyFilterMessage: 'No results found',
    searchMessage: '{0} results are available',
    selectionMessage: '{0} items selected',
    emptySelectionMessage: 'No selected item',
    emptySearchMessage: 'No results found',
    emptyMessage: 'No available options',
    aria: {
      trueLabel: 'True',
      falseLabel: 'False',
      nullLabel: 'Not Selected',
      star: '1 star',
      stars: '{star} stars',
      selectAll: 'All items selected',
      unselectAll: 'All items unselected',
      close: 'Close',
      previous: 'Previous',
      next: 'Next',
      navigation: 'Navigation',
      scrollTop: 'Scroll Top',
      moveTop: 'Move Top',
      moveUp: 'Move Up',
      moveDown: 'Move Down',
      moveBottom: 'Move Bottom',
      moveToTarget: 'Move to Target',
      moveToSource: 'Move to Source',
      moveAllToTarget: 'Move All to Target',
      moveAllToSource: 'Move All to Source',
      pageLabel: 'Page {page}',
      firstPageLabel: 'First Page',
      lastPageLabel: 'Last Page',
      nextPageLabel: 'Next Page',
      previousPageLabel: 'Previous Page',
      rowsPerPageLabel: 'Rows per page',
      jumpToPageDropdownLabel: 'Jump to Page Dropdown',
      jumpToPageInputLabel: 'Jump to Page Input',
      selectRow: 'Row Selected',
      unselectRow: 'Row Unselected',
      expandRow: 'Row Expanded',
      collapseRow: 'Row Collapsed',
      showFilterMenu: 'Show Filter Menu',
      hideFilterMenu: 'Hide Filter Menu',
      filterOperator: 'Filter Operator',
      filterConstraint: 'Filter Constraint',
      editRow: 'Row Edit',
      saveEdit: 'Save Edit',
      cancelEdit: 'Cancel Edit',
      listView: 'List View',
      gridView: 'Grid View',
      slide: 'Slide',
      slideNumber: '{slideNumber}',
      zoomImage: 'Zoom Image',
      zoomIn: 'Zoom In',
      zoomOut: 'Zoom Out',
      rotateRight: 'Rotate Right',
      rotateLeft: 'Rotate Left'
    }
  }
})

app.use(ConfirmationService)
app.use(ToastService)

// Register PrimeVue directives
app.directive('tooltip', Tooltip)

// Initialize API interceptors after all stores and services are configured.
// Pass useAuthStore directly so authStoreRef is set synchronously — avoids a race condition
// where the dynamic import resolves after a component's onMounted fires on direct page load.
initializeApiInterceptors(useAuthStore)
initializeGuestService()

// Resolve the current hostname to a shop brand BEFORE first paint so the SPA
// boots into the right theme/chrome. A failed lookup leaves the default
// Zaveato shell intact (see brand store for canonical-host short-circuit),
// but on WL hosts this MUST await — otherwise the first paint shows Zaveato
// branding for a beat and every WL /404 render carries "— Zaveato" in the
// title, which Google was picking up as the actual title of every URL.
const brandBootstrap = useBrandStore(pinia).bootstrap().catch((err: unknown) => {
  console.warn('Brand bootstrap failed; falling back to default shell.', err)
})

// Global error handler - NEVER redirect, show error on current page
app.config.errorHandler = (err, instance, info) => {
  console.error('🔴 Global error handler caught:', err)
  console.error('Component:', instance)
  console.error('Info:', info)

  // Log error to backend
  if (err instanceof Error) {
    errorService.logError(err, instance?.$options.name || 'Unknown Component', info)
    captureHandledError(err, {
      tags: { source: 'vue-errorHandler' },
      extra: { component: instance?.$options.name, info },
    })

    // Show error on current page instead of redirecting
    // Components should subscribe to globalErrors from the app store
    if (errorService.shouldReportToUser(err)) {
      const message = errorService.getUserFriendlyMessage(err)
      const componentName = instance?.$options.name || 'Unknown Component'

      // Import and use the app store to show global error
      import('./stores/app').then(({ useAppStore }) => {
        const appStore = useAppStore()
        appStore.showGlobalError(message, 'Something went wrong', componentName)
      }).catch(() => {
        // Fallback: log to console if store import fails
        console.error('Failed to show global error:', message)
      })
    }
  }
}

// Global warning handler
app.config.warnHandler = (msg, instance, trace) => {
  if (import.meta.env.DEV) {
    console.warn('⚠️ Vue warning:', msg)
    console.warn('Component:', instance)
    console.warn('Trace:', trace)
  }
}

// Global unhandled rejection handler - NEVER redirect, show error on current page
window.addEventListener('unhandledrejection', (event) => {
  console.error('🔴 Unhandled promise rejection:', event.reason)

  if (event.reason instanceof Error) {
    errorService.logError(event.reason, 'Promise', 'Unhandled rejection')
    captureHandledError(event.reason, { tags: { source: 'unhandledrejection' } })

    // Show error on current page instead of redirecting
    if (errorService.shouldReportToUser(event.reason)) {
      const message = errorService.getUserFriendlyMessage(event.reason)

      // Import and use the app store to show global error
      import('./stores/app').then(({ useAppStore }) => {
        const appStore = useAppStore()
        appStore.showGlobalError(message, 'Something went wrong', 'Promise rejection')
      }).catch(() => {
        // Fallback: log to console if store import fails
        console.error('Failed to show global error:', message)
      })
    }
  }

  // Prevent default browser error handling
  event.preventDefault()
})

// Wait for brand resolution before first paint (see brandBootstrap above).
// On canonical Zaveato hosts this resolves synchronously via a short-circuit
// and adds no measurable delay.
brandBootstrap.finally(() => {
  app.mount('#app')
})